/**
 * Central utility to detect logged-in user role across all portals
 * Enforces that only Patients can book tests and phlebotomy services.
 * Dynamically resolves custom roles assigned across all portals.
 */

function resolveRoleLabel(userObj, defaultLabel) {
  if (!userObj || typeof userObj !== 'object') return defaultLabel;
  const custom = userObj.custom_role || userObj.role_title || userObj.assigned_role || userObj.custom_role_title;
  if (custom && typeof custom === 'string' && custom.trim().length > 0) {
    return custom.trim();
  }
  if (userObj.role && typeof userObj.role === 'string') {
    const rawRole = userObj.role.trim();
    if (!['user', 'patient', 'guest'].includes(rawRole.toLowerCase())) {
      const formatted = rawRole.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return formatted;
    }
  }
  return defaultLabel;
}

export function getCurrentUserRole() {
  // Check Super Admin
  const adminToken = localStorage.getItem('admin_token') || localStorage.getItem('super_admin_token');
  const adminUser = localStorage.getItem('admin_user');
  if (adminToken || adminUser) {
    let email = '';
    let name = '';
    let u = {};
    try { 
      u = JSON.parse(adminUser || '{}');
      email = u.email || '';
      name = u.name || '';
    } catch(e){}
    const label = resolveRoleLabel(u, 'Super Admin');
    return { isPatient: false, isStaff: true, role: 'super_admin', label, name, email, dashboard: '/superadmin' };
  }

  // Check Lab Owner & Lab Operations Manager
  const labToken = localStorage.getItem('lab_token');
  const labUser = localStorage.getItem('lab_user') || localStorage.getItem('portal_user') || localStorage.getItem('user');
  if (labToken || localStorage.getItem('lab_user')) {
    let email = '';
    let name = '';
    let u = {};
    try {
      u = JSON.parse(labUser || '{}');
      email = u.email || '';
      name = u.name || (u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : '');
    } catch(e){}
    const rawRole = (u.role || '').toLowerCase();
    const isOwner = rawRole === 'lab_owner' || rawRole === 'tenant_owner' || rawRole === 'org_admin' || Boolean(u.is_lab_owner);
    const defaultLabel = isOwner ? 'Lab Owner' : 'Lab Manager';
    const label = resolveRoleLabel(u, defaultLabel);
    const role = isOwner ? 'lab_owner' : 'lab_manager';
    const dashboard = isOwner ? '/portal/lab-owner/dashboard' : '/portal/lab-manager/dashboard';
    return { isPatient: false, isStaff: true, role, label, name: name || label, email, dashboard };
  }

  // Check Phlebotomist / Specialist
  const phlebToken = localStorage.getItem('phleb_token');
  const phlebUser = localStorage.getItem('phleb_user');
  if (phlebToken || phlebUser) {
    let email = '';
    let name = '';
    let u = {};
    try { 
      u = JSON.parse(phlebUser || '{}');
      email = u.email || '';
      name = u.name || '';
    } catch(e){}
    const label = resolveRoleLabel(u, 'Specialist');
    return { isPatient: false, isStaff: true, role: 'phlebotomist', label, name, email, dashboard: '/portal/phlebotomist/dashboard' };
  }

  // Check Employer / HR Admin
  const employerToken = localStorage.getItem('employer_token');
  const employerUser = localStorage.getItem('employer_user');
  if (employerToken || employerUser) {
    let email = '';
    let name = '';
    let isHr = false;
    let u = {};
    try { 
      u = JSON.parse(employerUser || '{}');
      email = u.email || '';
      name = u.name || u.company_name || 'Employer';
      isHr = Boolean(u.is_hr_admin || u.role === 'hr_admin' || email === 'musb@hr.com');
    } catch(e){}
    const label = resolveRoleLabel(u, isHr ? 'HR Admin' : 'Employer');
    return {
      isPatient: false,
      isStaff: true,
      role: isHr ? 'hr_admin' : 'employer',
      label,
      name,
      email,
      dashboard: '/portal/employer'
    };
  }

  // Check Doctor / Physician
  const doctorToken = localStorage.getItem('doctor_token');
  const doctorUser = localStorage.getItem('doctor_user');
  if (doctorToken || doctorUser) {
    let email = '';
    let name = '';
    let u = {};
    try {
      u = JSON.parse(doctorUser || '{}');
      email = u.email || '';
      name = u.name || (u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : '');
    } catch(e){}
    const label = resolveRoleLabel(u, 'Doctor');
    return { isPatient: false, isStaff: true, role: 'doctor', label, name, email, dashboard: '/portal/doctor/dashboard' };
  }

  // Check Lab Technician
  const techToken = localStorage.getItem('technician_token');
  const techUser = localStorage.getItem('technician_user');
  if (techToken || techUser) {
    let email = '';
    let name = '';
    let u = {};
    try {
      u = JSON.parse(techUser || '{}');
      email = u.email || '';
      name = u.name || '';
    } catch(e){}
    const label = resolveRoleLabel(u, 'Technician');
    return { isPatient: false, isStaff: true, role: 'lab_technician', label, name, email, dashboard: '/portal/technician/dashboard' };
  }

  // Check Hub Fleet Manager
  const hubToken = localStorage.getItem('hub_token');
  const hubUser = localStorage.getItem('hub_user');
  if (hubToken || hubUser) {
    let email = '';
    let name = '';
    let u = {};
    try { 
      u = JSON.parse(hubUser || '{}');
      email = u.email || '';
      name = u.name || 'Hub Manager';
    } catch(e){}
    const label = resolveRoleLabel(u, 'Fleet Hub');
    return { isPatient: false, isStaff: true, role: 'fleet_hub', label, name, email, dashboard: '/portal/phlebotomy-hub' };
  }

  // Check Diagnostic Partner
  const diagToken = localStorage.getItem('diag_token');
  const diagUser = localStorage.getItem('diag_user');
  if (diagToken || diagUser) {
    let email = '';
    let name = '';
    let u = {};
    try { 
      u = JSON.parse(diagUser || '{}');
      email = u.email || '';
      name = u.name || '';
    } catch(e){}
    const label = resolveRoleLabel(u, 'Diagnostic Partner');
    return { isPatient: false, isStaff: true, role: 'diagnostic', label, name, email, dashboard: '/portal/diagnostic/dashboard' };
  }

  // Check Research Partner
  const resToken = localStorage.getItem('res_token') || localStorage.getItem('research_token');
  const resUser = localStorage.getItem('res_user') || localStorage.getItem('research_user');
  if (resToken || resUser) {
    let email = '';
    let name = '';
    let u = {};
    try { 
      u = JSON.parse(resUser || '{}');
      email = u.email || '';
      name = u.name || '';
    } catch(e){}
    const label = resolveRoleLabel(u, 'Research Partner');
    return { isPatient: false, isStaff: true, role: 'research', label, name, email, dashboard: '/portal/diagnostic/dashboard' };
  }

  // Check Patient (patient_user/patient_token or generic user/token)
  const patientToken = localStorage.getItem('patient_token') || localStorage.getItem('token');
  const patientUser = localStorage.getItem('patient_user') || localStorage.getItem('user');
  if (patientToken || patientUser) {
    let email = '';
    let name = '';
    let u = {};
    try { 
      u = JSON.parse(patientUser || '{}');
      email = u.email || '';
      name = u.name || (u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : '');
    } catch(e){}
    const label = resolveRoleLabel(u, 'Patient');
    return { isPatient: true, isStaff: false, role: 'patient', label, name, email, dashboard: '/portal/patient/dashboard' };
  }

  // Unauthenticated / Guest
  return { isPatient: false, isStaff: false, role: 'guest', label: 'Guest', name: '', email: '', dashboard: '/portal/login' };
}
